import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShoppingBag, Loader2, Leaf, Truck, ShieldCheck, Star, Play } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fcfa, PLACEHOLDER_IMG } from "../../lib/format";
import { ShopHeader } from "../../components/shop/ShopHeader";
import { ShopFooter } from "../../components/shop/ShopFooter";
import { useCart } from "../../context/CartContext";
import { toast } from "sonner";

function ytId(v) {
  if (!v) return "";
  const m = v.match(/(?:youtu\.be\/|v=|embed\/|shorts\/|live\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : (v.length === 11 ? v : "");
}

function Stars({ value, onChange, size = "w-5 h-5" }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={!onChange} onClick={() => onChange && onChange(n)}
          data-testid={onChange ? `star-${n}` : undefined}
          className={onChange ? "cursor-pointer" : "cursor-default"}>
          <Star className={`${size} ${n <= value ? "fill-brand text-brand" : "text-brand-line"}`} />
        </button>
      ))}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, openCart } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [reviews, setReviews] = useState({ average: 0, count: 0, reviews: [] });
  const [rv, setRv] = useState({ name: "", rating: 5, comment: "" });
  const [rvLoading, setRvLoading] = useState(false);

  const loadReviews = () => api.get(`/products/${id}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => setProduct(data)).catch(() => setNotFound(true));
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!rv.name.trim()) return toast.error("Votre nom est requis.");
    setRvLoading(true);
    try {
      await api.post(`/products/${id}/reviews`, rv);
      toast.success("Merci pour votre avis !");
      setRv({ name: "", rating: 5, comment: "" });
      loadReviews();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRvLoading(false);
    }
  };

  if (notFound) {
    return (
      <div className="App"><ShopHeader />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <p className="text-brand-muted">Produit introuvable.</p>
          <Link to="/boutique" className="text-brand font-semibold mt-4 inline-block">Retour à la boutique</Link>
        </div>
      </div>
    );
  }

  if (!product) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>;
  }

  const handleAdd = () => {
    addItem(product, qty);
    toast.success(`${qty} × ${product.name} ajouté au panier`);
    openCart();
  };

  return (
    <div className="App overflow-x-hidden">
      <ShopHeader />
      <div className="w-full max-w-md mx-auto sm:max-w-5xl px-4 sm:px-6 lg:px-8 py-6">
        <button onClick={() => navigate(-1)} data-testid="back-button" className="flex items-center gap-2 text-brand-sub hover:text-brand mb-6 font-medium">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <div className="grid sm:grid-cols-2 gap-8">
          <div className="relative rounded-3xl overflow-hidden aspect-square shadow-sm border border-slate-200" style={{ background: "linear-gradient(135deg,#ffffff,#f3f0fb)" }}>
            <img src={product.image_url || PLACEHOLDER_IMG} alt={product.name} className="w-full h-full object-contain p-6" />
            <span className="absolute top-4 left-4 text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full" style={{ background: "#C9A227", color: "#1a0c34" }}>
              NOUMABOU™
            </span>
            <span className={`absolute top-4 right-4 text-[11px] font-bold px-2.5 py-1 rounded-full ${product.coming_soon ? "text-brand-ink" : product.in_stock ? "bg-green-500 text-white" : "bg-rose-500 text-white"}`} style={product.coming_soon ? { background: "#C9A227" } : undefined}>
              {product.coming_soon ? "Bientôt disponible" : product.in_stock ? "✓ En stock" : "Rupture"}
            </span>
          </div>

          <div className="flex flex-col">
            <span className="text-xs font-bold tracking-[0.3em] uppercase" style={{ color: "#C9A227" }}>{product.category || "Complément naturel"}</span>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-brand-ink mt-2">{product.name}</h1>
            {product.coming_soon ? (
              <p className="font-heading font-bold text-lg mt-3" style={{ color: "#C9A227" }}>Prix communiqué au lancement</p>
            ) : (
              <div className="flex items-baseline gap-1.5 mt-3">
                <span className="font-heading font-black text-3xl" style={{ color: "#5B2A9D" }}>{fcfa(product.price).replace(" FCFA", "")}</span>
                <span className="text-sm font-semibold text-slate-400">FCFA</span>
              </div>
            )}
            {reviews.count > 0 && (
              <div className="flex items-center gap-2 mt-2">
                <Stars value={Math.round(reviews.average)} size="w-4 h-4" />
                <span className="text-sm text-brand-muted">{reviews.average}/5 · {reviews.count} avis</span>
              </div>
            )}
            <p className="text-base leading-relaxed text-brand-sub mt-4">{product.description}</p>

            {product.coming_soon ? (
              <div className="mt-8 rounded-2xl px-5 py-4 font-semibold text-brand-ink inline-flex items-center gap-2" style={{ background: "#C9A22722", border: "2px solid #C9A227" }}>
                Ce produit sera bientôt disponible.
              </div>
            ) : product.in_stock ? (
              <>
                <div className="flex items-center gap-4 mt-8">
                  <span className="text-sm font-medium text-brand-sub">Quantité</span>
                  <div className="flex items-center border border-brand-line rounded-full">
                    <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" className="p-2.5 hover:bg-brand-cream rounded-full"><Minus className="w-4 h-4" /></button>
                    <span data-testid="qty-value" className="w-10 text-center font-semibold">{qty}</span>
                    <button onClick={() => setQty(qty + 1)} data-testid="qty-plus" className="p-2.5 hover:bg-brand-cream rounded-full"><Plus className="w-4 h-4" /></button>
                  </div>
                </div>
                <button onClick={handleAdd} data-testid="add-to-cart-button" className="mt-6 text-white hover:brightness-110 transition-all rounded-full px-6 py-3.5 font-bold shadow-md flex items-center justify-center gap-2" style={{ background: "#5B2A9D" }}>
                  <ShoppingBag className="w-5 h-5" /> Ajouter au panier — {fcfa(product.price * qty)}
                </button>
              </>
            ) : (
              <p className="mt-8 text-brand-secondary font-semibold">Actuellement en rupture de stock.</p>
            )}

            <div className="flex flex-wrap gap-5 mt-8 text-sm text-brand-sub border-t border-brand-line pt-6">
              <span className="flex items-center gap-2"><Leaf className="w-4 h-4 text-brand-accent" /> Naturel</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-brand-accent" /> Tmoney · Flooz · Cash</span>
            </div>
          </div>
        </div>

        {/* Fiche produit — toujours visible */}
        <div className="mt-12 grid sm:grid-cols-2 gap-6 max-w-4xl">
          <div className="bg-white border border-brand-line rounded-2xl p-6">
            <h2 className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2"><Leaf className="w-5 h-5 text-brand-accent" /> Bienfaits</h2>
            <p className="text-brand-sub mt-3 whitespace-pre-line leading-relaxed">{product.benefits || "Informations bientôt disponibles."}</p>
          </div>
          <div className="bg-white border border-brand-line rounded-2xl p-6">
            <h2 className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-brand-accent" /> Mode d'emploi</h2>
            <p className="text-brand-sub mt-3 whitespace-pre-line leading-relaxed">{product.usage || "Informations bientôt disponibles."}</p>
          </div>
        </div>

        {/* Vidéo du produit — toujours visible */}
        <div className="mt-12 max-w-3xl">
          <h2 className="font-heading text-2xl font-bold text-brand-ink mb-4 flex items-center gap-2"><Play className="w-5 h-5 text-brand" /> Vidéo du produit</h2>
          {product.video_path ? (
            <div className="rounded-2xl overflow-hidden bg-black">
              <video className="w-full h-full" src={product.video_path} controls playsInline preload="metadata" />
            </div>
          ) : product.video_url && ytId(product.video_url) ? (
            <div className="rounded-2xl overflow-hidden aspect-video bg-black">
              <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${ytId(product.video_url)}`} title="Vidéo produit" allowFullScreen loading="lazy" />
            </div>
          ) : (
            <div className="rounded-2xl aspect-video border-2 border-dashed border-brand-line bg-brand-cream/50 flex flex-col items-center justify-center text-center px-6">
              <span className="w-14 h-14 rounded-full bg-brand/10 flex items-center justify-center"><Play className="w-7 h-7 text-brand" /></span>
              <p className="text-brand-sub font-medium mt-3">Vidéo de démonstration à venir</p>
              <p className="text-brand-muted text-sm mt-1">Une vidéo « produit en main » sera bientôt ajoutée ici.</p>
            </div>
          )}
        </div>

        {/* Avis */}
        <div className="mt-12 max-w-3xl">
          <h2 className="font-heading text-2xl font-bold text-brand-ink mb-4 flex items-center gap-2"><Star className="w-5 h-5 text-brand" /> Avis clients {reviews.count > 0 && `(${reviews.count})`}</h2>

          <form onSubmit={submitReview} className="bg-white border border-brand-line rounded-2xl p-5 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <input data-testid="review-name" value={rv.name} onChange={(e) => setRv({ ...rv, name: e.target.value })} placeholder="Votre nom" className="border border-brand-line rounded-xl px-4 py-2.5 flex-1 bg-white text-brand-ink outline-none focus:ring-2 focus:ring-brand-accent" />
              <div className="flex items-center gap-2"><span className="text-sm text-brand-sub">Note :</span><Stars value={rv.rating} onChange={(n) => setRv({ ...rv, rating: n })} /></div>
            </div>
            <textarea data-testid="review-comment" value={rv.comment} onChange={(e) => setRv({ ...rv, comment: e.target.value })} rows={2} placeholder="Votre avis (optionnel)" className="border border-brand-line rounded-xl px-4 py-2.5 w-full bg-white text-brand-ink outline-none focus:ring-2 focus:ring-brand-accent" />
            <button type="submit" disabled={rvLoading} data-testid="review-submit" className="bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-2.5 font-semibold flex items-center gap-2 disabled:opacity-60">
              {rvLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publier mon avis"}
            </button>
          </form>

          <div className="mt-5 space-y-3">
            {reviews.reviews.length === 0 ? (
              <p className="text-brand-muted text-sm">Soyez le premier à donner votre avis sur ce produit.</p>
            ) : reviews.reviews.map((r) => (
              <div key={r.id} data-testid={`review-${r.id}`} className="bg-white border border-brand-line rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-brand-ink">{r.name}</span>
                  <Stars value={r.rating} size="w-4 h-4" />
                </div>
                {r.comment && <p className="text-sm text-brand-sub mt-2">{r.comment}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
      <ShopFooter />
    </div>
  );
}
